import type { UserAction, UserState } from "./types";
import { canAccessStaffPortal, isUserDataComplete } from "./validation";
import {
  getUserFromCache,
  isCacheValid,
  saveUserToCache,
  userStorage,
} from "./storage";
import { USER_CACHE_TIMESTAMP_KEY } from "./constants";

export const createInitialState = (): UserState => ({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  lastFetch: null,
  isHydrated: false,
  isClientSideHydrated: false,
});

export const userReducer = (
  state: UserState,
  action: UserAction
): UserState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_USER": {
      const newUser = action.payload;
      const newState = {
        ...state,
        user: newUser,
        isAuthenticated: !!newUser,
        isHydrated: isUserDataComplete(newUser),
        error: null,
      };

      if (newUser) {
        saveUserToCache(newUser);
      } else {
        userStorage.clear();
      }

      return newState;
    }

    case "SET_USER_PARTIAL": {
      const newUser = action.payload;
      const newState = {
        ...state,
        user: newUser,
        isAuthenticated: !!newUser,
        isHydrated: false, // Partial data is never hydrated
        error: null,
      };

      if (newUser) {
        saveUserToCache(newUser);
      }

      return newState;
    }

    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false };

    case "SET_AUTHENTICATED":
      return { ...state, isAuthenticated: action.payload };

    case "SET_LAST_FETCH":
      return { ...state, lastFetch: action.payload };

    case "SET_HYDRATED":
      return { ...state, isHydrated: action.payload };

    case "HYDRATE_FROM_CACHE": {
      const cachedUser = getUserFromCache();
      const hasValidCache = cachedUser && isCacheValid();

      if (hasValidCache && cachedUser) {
        if (!canAccessStaffPortal(cachedUser)) {
          userStorage.clear();
          return {
            ...state,
            isClientSideHydrated: true,
          };
        }

        return {
          ...state,
          user: cachedUser,
          isAuthenticated: !!cachedUser,
          lastFetch: userStorage.get<number>(USER_CACHE_TIMESTAMP_KEY),
          isHydrated: isUserDataComplete(cachedUser),
          isClientSideHydrated: true,
        };
      }

      return {
        ...state,
        isClientSideHydrated: true,
      };
    }

    case "LOGOUT": {
      userStorage.clear();
      // Preserve isClientSideHydrated to prevent hydration mismatch after logout
      return {
        ...createInitialState(),
        isClientSideHydrated: state.isClientSideHydrated,
      };
    }

    case "RESET": {
      userStorage.clear();
      // Preserve isClientSideHydrated to prevent hydration mismatch after reset
      return {
        ...createInitialState(),
        isClientSideHydrated: state.isClientSideHydrated,
      };
    }

    default:
      return state;
  }
};
